import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import MasteryBar from '../components/MasteryBar';
import SideBadge from '../components/SideBadge';
import Chessboard, { type BoardTheme } from '../board/Chessboard';
import { useAuth } from '../auth/AuthContext';
import { studyApi } from '../api/studyApi';
import type { RepertoireEntry, StudyStats, UserSettings } from '../../../shared/types';

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const THEMES: BoardTheme[] = ['walnut', 'slate', 'green', 'ice'];

/** Progress, repertoire management and study preferences in one place. */
export default function ProfilePage() {
  const { user, getToken, refresh } = useAuth();
  const auth = useMemo(() => ({ getToken, refresh }), [getToken, refresh]);

  const [stats, setStats] = useState<StudyStats | null>(null);
  const [repertoire, setRepertoire] = useState<RepertoireEntry[]>([]);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Stats and repertoire come from the one combined request; settings is the
    // only extra thing this page needs.
    Promise.all([studyApi.home(auth), studyApi.settings(auth)])
      .then(([home, cfg]) => {
        if (cancelled) return;
        setStats(home.stats);
        setRepertoire(home.repertoire);
        setSettings(cfg);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load your profile'));
    return () => {
      cancelled = true;
    };
  }, [auth]);

  async function save(patch: Partial<UserSettings>) {
    if (!settings) return;
    const optimistic = { ...settings, ...patch };
    setSettings(optimistic);
    try {
      // The server clamps out-of-range values, so trust its answer over ours.
      const confirmed = await studyApi.updateSettings(patch, auth);
      setSettings(confirmed);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that setting');
    }
  }

  async function remove(openingId: string) {
    try {
      await studyApi.removeFromRepertoire(openingId, auth);
      setRepertoire((list) => list.filter((e) => e.openingId !== openingId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that opening');
    }
  }

  return (
    <>
      <SiteHeader />
      <main className="page">
        <h1>{user?.displayName}</h1>
        <p className="muted">{user?.email}</p>

        {error && <p className="error">{error}</p>}

        <section>
          <h2>Progress</h2>
          <div className="stat-row">
            <Stat value={stats?.currentStreak ?? 0} label="day streak" />
            <Stat value={stats?.longestStreak ?? 0} label="longest streak" />
            <Stat value={stats?.totalReviews ?? 0} label="total reviews" />
            <Stat value={stats?.linesLearned ?? 0} label="lines learned" />
          </div>
          {stats?.lastStudyDate && (
            <p className="muted">Last studied {stats.lastStudyDate}.</p>
          )}
        </section>

        <section>
          <div className="section-head">
            <h2>Repertoire</h2>
            <Link className="section-link" to="/openings">Add an opening →</Link>
          </div>
          {repertoire.length === 0 ? (
            <p className="muted">You are not studying any openings yet.</p>
          ) : (
            <ul className="settings-list">
              {repertoire.map((entry) => (
                <li key={entry.openingId} className="settings-row">
                  <div className="settings-row-main">
                    <Link to={`/openings/${entry.openingId}`} className="rep-name">{entry.name}</Link>
                    <SideBadge side={entry.side} />
                    <MasteryBar value={entry.mastery} compact />
                  </div>
                  <button type="button" className="btn btn--small btn--danger" onClick={() => remove(entry.openingId)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="muted">
            Removing an opening stops it being scheduled but keeps your progress, so adding it back
            later picks up where you left off.
          </p>
        </section>

        {settings && (
          <section>
            <div className="section-head">
              <h2>Study settings</h2>
              {saved && <span className="saved-flash">Saved</span>}
            </div>

            <div className="settings-grid">
              <label className="field">
                Suggested new lines per day
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={settings.newPerDay}
                  onChange={(e) => save({ newPerDay: Number(e.target.value) })}
                />
                <span className="field-hint">
                  A pace, not a cap — sessions never withhold a variation from you. Each new
                  line returns for review tomorrow, so a big day now means a big day later.
                </span>
              </label>

              <label className="field">
                Reviews per session
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={settings.reviewLimit}
                  onChange={(e) => save({ reviewLimit: Number(e.target.value) })}
                />
                <span className="field-hint">Caps the length of one study session.</span>
              </label>

              <label className="field">
                Opponent reply delay
                <input
                  type="range"
                  min={0}
                  max={1500}
                  step={50}
                  value={settings.opponentDelayMs}
                  onChange={(e) => save({ opponentDelayMs: Number(e.target.value) })}
                />
                <span className="field-hint">{settings.opponentDelayMs} ms before the reply appears.</span>
              </label>

              <label className="field field--checkbox">
                <input
                  type="checkbox"
                  checked={settings.showCoordinates}
                  onChange={(e) => save({ showCoordinates: e.target.checked })}
                />
                Show board coordinates
              </label>
            </div>

            <h3>Board theme</h3>
            <div className="theme-picker">
              {THEMES.map((theme) => (
                <button
                  key={theme}
                  type="button"
                  className={`theme-option${settings.boardTheme === theme ? ' theme-option--active' : ''}`}
                  onClick={() => save({ boardTheme: theme })}
                  aria-pressed={settings.boardTheme === theme}
                >
                  <Chessboard
                    fen={START_FEN}
                    orientation="white"
                    disabled
                    theme={theme}
                    showCoordinates={false}
                    maxSize={110}
                  />
                  <span className="theme-name">{theme}</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="stat">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}
